import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'src/redux/store';
import { paths, planNameCookie } from 'src/common/config/TextMessage';
import {
  hasLocalSeoCookie,
  removeLocalSeoCookie,
  setLocalSeoCookie,
  setUtmCookie
} from 'src/common/config/FunctionHelperPartTwo';

/**
 * Keeps browser cookies in sync with Redux subscription/plan state:
 *
 * - **Local SEO session cookie**: set when the active plan is Local SEO,
 *   removed when the plan changes away from Local SEO (except on billing pages).
 * - **UTM plan-name cookie**: updated whenever `activePlanName` changes,
 *   used for attribution tracking.
 */
export const useCookieSync = () => {
  const subscription = useSelector((state: RootState) => state.user.subscription);
  const activePlanName = useSelector((state: RootState) => state.user.activePlan);
  const planDetails = useSelector((state: RootState) => state.user.planDetails);

  // Sync Local SEO session cookie with the user's current plan
  useEffect(() => {
    try {
      const isLocalSeoSession = hasLocalSeoCookie();
      const isBillingPage =
        window.location.pathname === paths.billing ||
        window.location.pathname === paths.googleBusiness;

      if (
        subscription?.subscription_id &&
        activePlanName !== planDetails?.localseo &&
        isLocalSeoSession &&
        !isBillingPage
      ) {
        removeLocalSeoCookie();
      } else if (
        subscription?.subscription_id &&
        activePlanName === planDetails?.localseo &&
        !isLocalSeoSession
      ) {
        setLocalSeoCookie();
      }
    } catch {
      // Silently ignore cookie errors (e.g. browser privacy restrictions)
    }
  }, [subscription, activePlanName, planDetails]);

  // Keep the UTM plan-name cookie current for attribution
  useEffect(() => {
    if (activePlanName) {
      setUtmCookie(planNameCookie, activePlanName);
    }
  }, [activePlanName]);
};
